import type { FC, ReactNode } from "react";
import { memo } from "react";

import Typography from "@/design-system/Typography";
import PulseLoading from "@/design-system/pulse-loading";
import TelemetryButton from "@/design-system/telemetry-button";
import type { ISpecialCashback } from "@/src/cashback/CashbackCommonInterfaces";
import { LikeCounter } from "@/src/common/LikeCounter";
import { languageSelector } from "@/src/contexts";
import { InfoLabel } from "@/src/shared/InfoLabel";
import ImageLoader, { type IImageLoader } from "@Components/ImageLoader";
import { type TelemetryClickFromType } from "@Components/Modules/Helpers";
import { DiscountTileV3Resources } from "@Components/discount-tile/discount-tile-v3.Resources";
import { DiscountTileV3ResourcesEn } from "@Components/discount-tile/discount-tile-v3.Resources.En";
import { useTelemetry } from "@Components/telemetry-hoc/useTelemetry";
import * as stylex from "@stylexjs/stylex";

import {
    type PropsWithStyleX,
    type QueryXsMax,
    colors,
    genericStyles,
    globalLimitStyles,
} from "../../../styles/tokens.stylex";

export enum DiscountTypeEnum {
    // Undefined,
    Dedicated = 1,
    Promotion = 2,
    Sale = 3,
    New = 4,
    Event = 5,
    Flyer = 6,
}

export interface IDiscountTileV3 extends PropsWithStyleX {
    id: string;
    title?: string;
    brandId: string;
    brandName?: string;
    brandLogo: string;
    likesCount?: number;
    isLiked?: boolean;
    isFlyer?: boolean;
    discountType: DiscountTypeEnum;
    discountImageLoader: IImageLoader;
    imageBackgroundColor?: string;
    loyaltyProgramName?: string;
    maxCashback?: string;
    specialCashback?: ISpecialCashback;
    navigateDiscountUrl?: string;
    navigateBrandUrl?: string;
    onClick?: (_event: React.MouseEvent<HTMLAnchorElement>, _tileId: string) => boolean | void;
    onBrandClick?: (_event: React.MouseEvent<HTMLAnchorElement>, _tileId: string) => boolean | void;
    origin?: TelemetryClickFromType;
    customContent?: ReactNode;
}

const DiscountTileV3: FC<IDiscountTileV3> = memo((props: IDiscountTileV3) => {
    const resources = languageSelector(DiscountTileV3Resources, DiscountTileV3ResourcesEn);

    const telemetryRef = useTelemetry<HTMLDivElement>({
        eventName: "common-discountView",
        actionType: "View",
        itemId: props.id,
        origin: props.origin,
    });

    const onClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (props.onClick) {
            props.onClick(event, props.id);
        }
    };

    const onBrandClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (props.onBrandClick) {
            props.onBrandClick(event, props.id);
        }
    };

    const renderDiscountImage = () => (
        <div {...stylex.props(styles.discountImageContainer)}>
            {props.discountType ? (
                <span {...stylex.props(styles.discountType, discountTypeStyleX(props.discountType))}>
                    {
                        resources.discountType[
                            DiscountTypeEnum[props.discountType] as keyof typeof DiscountTypeEnum
                        ]
                    }
                </span>
            ) : null}
            <div {...stylex.props(styles.discountImage)}>
                <ImageLoader
                    {...props.discountImageLoader}
                    style={{ backgroundColor: props.imageBackgroundColor }}
                    srcSetAxis="width"
                    size={600}
                    lazyLoading={true}
                />
            </div>
        </div>
    );

    const renderBrandImage = () => (
        <TelemetryButton
            as="a"
            variant="none"
            size="None"
            target="_self"
            id={props.id}
            title={props.title}
            href={props.navigateBrandUrl}
            eventName="common-brandClick"
            itemId={props.brandId}
            onClick={onBrandClick}
            styleX={styles.brandImageContainer}
        >
            <div {...stylex.props(styles.bandImageBorder(props.imageBackgroundColor || "#fff"))}>
                <div ref={telemetryRef} {...stylex.props(styles.brandImageBorder)}>
                    <ImageLoader
                        lazyLoading
                        src={props.brandLogo || ""}
                        alt={props.brandId}
                        srcSetAxis="width"
                        size={300}
                    />
                </div>
            </div>
        </TelemetryButton>
    );

    const renderDiscountDetails = () => (
        <div {...stylex.props(styles.discountDetails)}>
            <div {...stylex.props(styles.discountDescription)}>
                {(!!props.title || !!props.brandName) && (
                    <div {...stylex.props(styles.discountTitle)}>
                        {props.brandName && (
                            <Typography
                                fontStyle="BodyL"
                                styleX={[styles.brandName, globalLimitStyles.TextOneLinesCap]}
                            >
                                {props.brandName}
                            </Typography>
                        )}
                        {props.title && (
                            <Typography
                                fontStyle="BodyM"
                                styleX={(styles.titleText, globalLimitStyles.TextOneLinesCap)}
                            >
                                {props.title}
                            </Typography>
                        )}
                    </div>
                )}
                <div {...stylex.props(styles.additionalInfo)}>
                    {props.likesCount !== undefined && (
                        <LikeCounter
                            id={props.id}
                            isSaved={props.isLiked || false}
                            likesCount={props.likesCount}
                        />
                    )}
                    {props.loyaltyProgramName && (
                        <InfoLabel iconType="LoyaltyCard" labelType="blue" text={props.loyaltyProgramName} />
                    )}
                    {props.specialCashback && (
                        <InfoLabel
                            iconType="Cashback"
                            labelType="magenta"
                            specialCashback={props.specialCashback}
                            text={props.maxCashback || ""}
                        />
                    )}
                    {props.isFlyer && (
                        <InfoLabel iconType="Flyer" labelType="green" text={resources.flyerLabel} />
                    )}
                </div>
            </div>
        </div>
    );

    const renderContent = () => (
        <>
            <TelemetryButton
                as="a"
                target="_self"
                variant="none"
                size="None"
                id={props.id}
                title={props.title}
                href={props.navigateDiscountUrl}
                eventName={
                    props.origin && props.origin !== "Undefined" && props.origin !== "SearchResult"
                        ? "common-discountClick"
                        : null
                }
                itemId={props.id}
                onClick={onClick}
            >
                {renderDiscountImage()}
                {renderDiscountDetails()}
            </TelemetryButton>
            {renderBrandImage()}
        </>
    );

    const renderLoadingEffect = () => (
        <>
            <PulseLoading stylexItem={styles.discountImageContainer} />
            <div {...stylex.props(styles.discountDetails)}>
                <div {...stylex.props(styles.discountDescription)}>
                    <PulseLoading stylexItem={styles.discountTitle} pulseNumber={2} />
                </div>
                <div {...stylex.props(styles.brandImageContainer)}>
                    <div {...stylex.props(styles.brandImageBorder)}>
                        <PulseLoading />
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <div {...stylex.props(styles.tile, props.styleX)}>
            {props.id ? renderContent() : renderLoadingEffect()}
        </div>
    );
});

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const distancePercent = "50%";

const styles = stylex.create({
    tile: {
        display: "block",
        fontSize: "0.875rem",
        position: "relative",
        height: "100%",
        width: "100%",
    },

    discountImageContainer: {
        borderRadius: 8,
        overflow: "hidden",
        alignItems: "center",
        boxShadow: "inset 0 0 10px 0 rgba(0,0,0,0.2)",
        display: "flex",
        position: "relative",
        paddingTop: distancePercent,
        width: "100%",
    },

    discountImage: {
        alignItems: "center",

        contain: "strict",
        display: "flex",
        position: "absolute",
        height: "100%",
        top: 0,
        width: "100%",
    },

    brandImageContainer: {
        position: "absolute",
        paddingTop: {
            default: `calc(${distancePercent} - 40px)`,
            [xsMax]: `calc(${distancePercent} - calc(25vw - 10px)/2)`,
        },
        right: 20,
        top: 0,
    },

    bandImageBorder: (color: string) => ({
        borderColor: color,
        borderRadius: genericStyles.borderRadiusFullRound,
        borderStyle: "solid",
        borderWidth: 6,
    }),

    brandImageBorder: {
        padding: { default: 12, [xsMax]: "15%" },
        borderRadius: genericStyles.borderRadiusFullRound,
        alignItems: "center",
        backgroundColor: colors.bg_Primary,
        display: "flex",
        justifyContent: "center",
        outlineColor: "#b4b4b4",
        outlineStyle: "solid",
        outlineWidth: "1px",
        height: { default: 80, [xsMax]: "calc(25vw - 10px)" },
        width: { default: 80, [xsMax]: "calc(25vw - 10px)" },
    },

    discountDetails: {
        position: "relative",
    },

    discountDescription: {
        borderRadius: 8,

        textAlign: "left",
        paddingTop: 10,
    },
    discountTitle: {
        height: { default: 60, [xsMax]: 55 },
        width: { default: "calc(100% - 100px)", [xsMax]: "calc(100% - (25vw + 15px))" },
    },
    brandName: {
        fontSize: { default: "1rem", [xsMax]: "0.875rem" },

        fontWeight: 700,
        lineHeight: { default: "18px", [xsMax]: "16px" },
        marginBottom: { default: 10, [xsMax]: 6 },
    },
    titleText: {
        margin: 0,
        color: colors.fg_Primary,
        fontSize: { default: "0.875rem", [xsMax]: "0.75rem" },
        lineHeight: 1,
    },
    additionalInfo: {
        alignItems: "center",
        columnGap: 20,
        display: "flex",
        rowGap: 20,
    },

    discountType: {
        alignItems: "center",
        color: colors.b_Emphasis,
        display: "flex",
        fontSize: { default: null, [xsMax]: "0.625rem" },
        position: "absolute",
        textAlign: "center",
        textTransform: "uppercase",
        zIndex: 1,
        borderBottomColor: colors.fg_AltDefault,
        borderBottomLeftRadius: "0",
        borderBottomRightRadius: "3px",
        borderBottomStyle: "solid",
        borderBottomWidth: 4,
        borderRightColor: colors.fg_AltDefault,
        borderRightStyle: "solid",
        borderRightWidth: 4,
        borderTopLeftRadius: "0",
        borderTopRightRadius: "0",
        height: "2em",
        left: 0,
        paddingBottom: { default: 0, [xsMax]: 0 },
        paddingLeft: { default: 15, [xsMax]: 25 },
        paddingRight: { default: 15, [xsMax]: 25 },
        paddingTop: { default: 0, [xsMax]: 0 },
        top: 0,
    },
});

export const discountTypeStyles = stylex.create({
    discount: {
        backgroundColor: colors.bg_BrandPrimary,
    },

    sale: {
        backgroundColor: "#0097a7",
    },

    newDiscount: {
        backgroundColor: "#ff4081",
    },

    flyer: {
        backgroundColor: "#ffb300",
    },

    moment: {
        backgroundColor: colors.fg_Accent,
    },
});

export const discountTypeStyleX = (type?: DiscountTypeEnum) => {
    switch (type) {
        case DiscountTypeEnum.Dedicated:
        case DiscountTypeEnum.Promotion:
            return discountTypeStyles.discount;
        case DiscountTypeEnum.Sale:
            return discountTypeStyles.sale;
        case DiscountTypeEnum.New:
            return discountTypeStyles.newDiscount;
        case DiscountTypeEnum.Flyer:
            return discountTypeStyles.flyer;
        case DiscountTypeEnum.Event:
            return discountTypeStyles.moment;
        default:
            return undefined;
    }
};

export default DiscountTileV3;
